import { useState, useEffect, useCallback, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../stores';
import type { UserRole } from '../../stores';

interface AdminUser {
  id: string;
  email: string;
  name: string;
  surname: string;
  fullName: string;
  role: UserRole;
}

interface CreateUserForm {
  email: string;
  password: string;
  name: string;
  surname: string;
  role: string;
}

const emptyForm = (defaultRole = 'viewer'): CreateUserForm => ({
  email: '',
  password: '',
  name: '',
  surname: '',
  role: defaultRole,
});

function generatePassword(length = 16): string {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghijkmnopqrstuvwxyz';
  const digits = '23456789';
  const symbols = '!@#$%&*?';
  const all = upper + lower + digits + symbols;

  const pick = (chars: string) => chars[Math.floor(Math.random() * chars.length)]!;
  const required = [pick(upper), pick(lower), pick(digits), pick(symbols)];

  const bytes = new Uint32Array(Math.max(length - required.length, 0));
  crypto.getRandomValues(bytes);
  const rest = Array.from(bytes, (n) => all[n % all.length]!);

  const chars = [...required, ...rest];
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [chars[i], chars[j]] = [chars[j]!, chars[i]!];
  }
  return chars.join('');
}

export function Users() {
  const { accessToken, user: currentUser } = useAuthStore();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState<CreateUserForm>(emptyForm());
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [generatedHint, setGeneratedHint] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch('/api/admin/users', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    }
  }, [accessToken]);

  const fetchRoles = useCallback(async () => {
    try {
      const response = await fetch('/api/admin/roles', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch roles');
      }

      const data = await response.json();
      setRoles(data);
    } catch (err) {
      console.error('Failed to fetch roles:', err);
    }
  }, [accessToken]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchUsers(), fetchRoles()]);
      setLoading(false);
    };
    loadData();
  }, [fetchUsers, fetchRoles]);

  const handleOpenModal = () => {
    const defaultRole = roles.includes('viewer')
      ? 'viewer'
      : roles[0] ?? 'viewer';
    setFormData(emptyForm(defaultRole));
    setFormError('');
    setGeneratedHint(false);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    if (saving) return;
    setShowModal(false);
    setFormError('');
    setGeneratedHint(false);
  };

  const handleGeneratePassword = async () => {
    const password = generatePassword(16);
    setFormData((prev) => ({ ...prev, password }));
    setGeneratedHint(true);
    try {
      await navigator.clipboard.writeText(password);
    } catch {
      // Clipboard may be denied; password still fills the field.
    }
  };

  const handleCreateUser = async (e: FormEvent) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);
    setError('');

    try {
      const response = await fetch('/api/admin/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          email: formData.email.trim(),
          password: formData.password,
          name: formData.name.trim(),
          surname: formData.surname.trim(),
          role: formData.role,
        }),
      });

      const data = (await response.json().catch(() => ({}))) as {
        message?: string;
        id?: string;
        email?: string;
        name?: string;
        surname?: string;
        fullName?: string;
        role?: string;
      };

      if (!response.ok) {
        throw new Error(data.message || 'Kullanıcı oluşturulamadı');
      }

      if (data.id && data.email) {
        setUsers((prev) => [
          {
            id: data.id!,
            email: data.email!,
            name: data.name ?? '',
            surname: data.surname ?? '',
            fullName: data.fullName || `${data.name ?? ''} ${data.surname ?? ''}`.trim() || data.email!,
            role: (data.role as UserRole) || (formData.role as UserRole),
          },
          ...prev,
        ]);
      } else {
        await fetchUsers();
      }

      setShowModal(false);
      setGeneratedHint(false);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Kullanıcı oluşturulamadı');
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    setActionLoading(userId);
    setError('');

    try {
      const response = await fetch(`/api/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ role: newRole }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to update role');
      }

      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, role: newRole as UserRole } : u))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteUser = async (userId: string, userName: string) => {
    if (!confirm(`"${userName}" kullanıcısını silmek istediğinize emin misiniz?`)) {
      return;
    }

    setActionLoading(userId);
    setError('');

    try {
      const response = await fetch(`/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to delete user');
      }

      setUsers((prev) => prev.filter((u) => u.id !== userId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setActionLoading(null);
    }
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'system':
        return 'role-badge role-system';
      case 'admin':
        return 'role-badge role-admin';
      case 'editor':
        return 'role-badge role-editor';
      default:
        return 'role-badge role-viewer';
    }
  };

  if (loading) {
    return <div className="admin-loading">Yükleniyor...</div>;
  }

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <div>
          <h1>Users</h1>
          <p>Kullanıcı hesaplarını ve rollerini yönetin</p>
        </div>
        <button type="button" className="admin-btn admin-btn-primary" onClick={handleOpenModal}>
          + Yeni Kullanıcı
        </button>
      </header>

      {error && <div className="admin-error">{error}</div>}

      <section className="admin-table-section">
        <div className="admin-table-toolbar">
          <h2>Kullanıcılar ({users.length})</h2>
        </div>

        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    <Link to={`/admin/users/${user.id}`} className="user-info user-info-link">
                      <div className="user-avatar">
                        {(user.name || user.email).charAt(0).toUpperCase()}
                      </div>
                      <div className="user-details">
                        <span className="user-name">{user.fullName || user.email}</span>
                        {user.id === currentUser?.id && (
                          <span className="user-you-badge">You</span>
                        )}
                      </div>
                    </Link>
                  </td>
                  <td>
                    <span className="user-email">{user.email}</span>
                  </td>
                  <td>
                    <select
                      className={getRoleBadgeClass(user.role)}
                      value={user.role}
                      onChange={(e) => handleRoleChange(user.id, e.target.value)}
                      disabled={actionLoading === user.id || user.id === currentUser?.id}
                      aria-label={`Role for ${user.fullName}`}
                    >
                      {roles.map((role) => (
                        <option key={role} value={role}>
                          {role}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <div className="admin-row-actions">
                      <Link
                        to={`/admin/users/${user.id}`}
                        className="admin-btn admin-btn-secondary admin-btn-tiny"
                      >
                        Detay
                      </Link>
                      <button
                        className="admin-delete-btn"
                        onClick={() => handleDeleteUser(user.id, user.fullName || user.email)}
                        disabled={actionLoading === user.id || user.id === currentUser?.id}
                        title={user.id === currentUser?.id ? 'You cannot delete yourself' : 'Delete user'}
                      >
                        {actionLoading === user.id ? '...' : '🗑'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {showModal && (
        <div className="admin-modal-overlay" onClick={handleCloseModal}>
          <div className="admin-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>Yeni Kullanıcı</h2>
              <button type="button" className="admin-modal-close" onClick={handleCloseModal}>
                ✕
              </button>
            </div>
            <form onSubmit={handleCreateUser} className="admin-form">
              {formError && <div className="admin-error">{formError}</div>}

              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Ad
                    <input
                      className="admin-form-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Ada"
                      autoComplete="off"
                    />
                  </label>
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Soyad
                    <input
                      className="admin-form-input"
                      value={formData.surname}
                      onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
                      placeholder="Lovelace"
                      autoComplete="off"
                    />
                  </label>
                </div>
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">
                  Email *
                  <input
                    type="email"
                    className="admin-form-input"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    placeholder="user@example.com"
                    autoComplete="off"
                  />
                </label>
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">
                  Şifre *
                  <div className="admin-password-field">
                    <input
                      type="text"
                      className="admin-form-input"
                      value={formData.password}
                      onChange={(e) => {
                        setFormData({ ...formData, password: e.target.value });
                        setGeneratedHint(false);
                      }}
                      required
                      minLength={6}
                      placeholder="En az 6 karakter"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="admin-btn admin-btn-secondary"
                      onClick={handleGeneratePassword}
                    >
                      Üret
                    </button>
                  </div>
                </label>
                {generatedHint ? (
                  <p className="admin-form-hint">
                    Şifre üretildi ve panoya kopyalandı. Kullanıcıya iletmeyi unutmayın.
                  </p>
                ) : null}
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">
                  Rol
                  <select
                    className="admin-form-select"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  >
                    {roles.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="admin-form-actions">
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary"
                  onClick={handleCloseModal}
                  disabled={saving}
                >
                  İptal
                </button>
                <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
                  {saving ? 'Kaydediliyor…' : 'Kullanıcı Oluştur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
